import { LandingComponentProps, LandingScreenSchema } from '@/modules/Landing/instances/@types/landing-types'

// The redesigned Kova landing (DEV-35932, a bare /app-v2) on /app-v2 — the universal variant:
// mobile Figma 6357:60725, desktop 6547:16807. The medication-agnostic page served by a bare /app-v2: both compounds in the hero and a card each
// below; the desktop frame (6547:16807) has no included-benefits card.
const schema: LandingScreenSchema = {
	config: { background: 'page' },

	order: [
		'LandingKovaV2Hero',
		'LandingKovaV2Trust',
		'LandingKovaV2HowItWorks',
		'LandingKovaV2Treatment',
		'LandingKovaV2Testimonials',
		'LandingKovaV2Providers',
		'LandingKovaV2Benefits',
		'LandingKovaV2Outcomes',
		'LandingKovaFaq',
		'LandingKovaV2FinalOffer',
		'LandingKovaFooter',
	],

	components: {
		LandingKovaV2Hero: (): LandingComponentProps.KovaV2Hero => ({
			ribbon: 'Lifetime Price Lock: Same price forever!',
			headerCtaLabel: 'Check my eligibility',
			rating: { score: '4.8', label: 'Excellent!', caption: 'Over 200,000+ happy customers' },
			title: 'Get GLP-1 treatment online — no in-person clinic visit or insurance required',
			price: { label: 'Plans from', now: '$129', was: '$299', period: 'per month' },
			description: 'Doctor-prescribed medication delivered to your door with free shipping.',
			features: ['No insurance required', 'Online clinician review included', 'Free shipping', 'Transparent pricing'],
			inStock: 'In-Stock',
			product: 'universal',
		}),

		LandingKovaV2Trust: (): LandingComponentProps.KovaV2Trust => ({
			items: [
				{ icon: 'legitscript', label: 'LegitScript certified' },
				{ icon: 'clinicians', label: 'US-licensed clinicians' },
				{ icon: 'hipaa', label: 'HIPAA aligned' },
				{ icon: 'eligibility', label: 'Eligibility before charge' },
			],
		}),

		LandingKovaV2HowItWorks: (): LandingComponentProps.KovaV2HowItWorks => ({
			title: 'How Kova works',
			steps: [
				{
					label: 'Step 1',
					title: 'Check your eligibility',
					text: 'Tell us about your health history, goals and preferences.',
				},
				{
					label: 'Step 2',
					title: 'Get clinician review',
					text: 'A licensed provider reviews your information and assesses your eligibility.',
				},
				{
					label: 'Step 3',
					title: 'Receive your medication',
					text: 'Your medication and supplies are delivered to your door with ongoing support from Kova.',
				},
				{
					label: 'Step 4',
					title: 'Start your plan',
					text: 'Our care team stays with you, with in-app support and progress tracking tools.',
				},
			],
		}),

		LandingKovaV2Treatment: (): LandingComponentProps.KovaV2Treatment => ({
			titleParts: [{ text: 'Medications ' }, { text: 'In Stock ', accent: true }, { text: 'Ready to Ship' }],
			medications: [
				{
					kind: 'semaglutide',
					name: 'Compounded Semaglutide',
					description: 'A once-weekly GLP-1 treatment option.',
					badge: { icon: 'lightning', label: 'More Affordable', tone: 'mint' },
				},
				{
					kind: 'tirzepatide',
					name: 'Compounded Tirzepatide',
					description: 'A dual-action GLP-1/GIP treatment option.',
					badge: { icon: 'clock', label: 'Fastest Results', tone: 'purple' },
				},
			],
			included: {
				title: '+ Everything you need',
				items: [
					'Medication and supplies.',
					'Instructions for use.',
					'Online clinician review.',
					'Ongoing medical support.',
					'Kova companion app.',
					'Treatment reminders and check-ins.',
					'Easy options to manage or cancel your plan, when eligible',
				],
				shipping: 'Free shipping',
				hiddenOnDesktop: true,
			},
			ctaLabel: 'Check my eligibility',
		}),

		LandingKovaV2Testimonials: (): LandingComponentProps.KovaV2Testimonials => ({
			title: '200k+ Happy Customers\u00a0:)',
			// Standalone cards 6557:32128 … 32233. Rachel's and Brian's figures and quotes are the
			// frames' own; the figures differ from the quote text in those frames.
			items: [
				{
					customer: 'sarah',
					name: 'Sarah',
					result: 'lost 90lbs with Kova',
					quote:
						'“Lost 90 pounds with Kova and the difference is huge. Less brain fog, more energy, and I finally feel like myself again!”',
				},
				{
					customer: 'james',
					name: 'James',
					result: 'lost 48lbs with Kova',
					quote:
						'“Losing the weight and seeing fuller hair has been such a confidence boost. I’m 48 pounds down with Kova and didn’t expect to look this good.”',
				},
				{
					customer: 'rachel',
					name: 'Rachel',
					result: 'lost 51lbs with Kova',
					quote:
						'“Lost 90 pounds with Kova and the difference is huge. Less brain fog, more energy, and I finally feel like myself again!”',
				},
				{
					customer: 'chris',
					name: 'Chris',
					result: 'lost 51lbs with Kova',
					quote:
						'“I wasn’t eating that badly, but the weight kept creeping up. By 3pm I was wiped out, sleeping poorly, and struggling to focus. Kova helped turn things around.”',
				},
				{
					customer: 'brian',
					name: 'Brian',
					result: 'lost 60lbs with Kova',
					quote:
						'“Nothing seemed to work for me anymore. With Kova, the weight finally started coming off, and I have so much more energy throughout the day. 42 lbs down and still going.”',
				},
			],
		}),

		LandingKovaV2Providers: (): LandingComponentProps.KovaV2Providers => ({
			title: 'Meet your providers',
			// Standalone cards 6461:63078 / 63109 / 63152 / 63168.
			items: [
				{
					provider: 'laurie',
					name: 'Laurie Mercier, MD',
					bio: 'Dr. Mercier has more than 25 years of experience in primary care. She initially graduated from college with a degree in Chemistry and Biochemistry and was awarded a National Science Foundation Grant to conduct further research at the Massachusetts Institute of Technology. She later earned a degree in Naturopathic Medicine from Bastyr University and practiced holistic medicine for several years while teaching Organic Chemistry, Immunology, Pharmacognosy, and other subjects.',
				},
				{
					provider: 'kimberli',
					name: 'Kimberli Hastings, NP',
					bio: 'Kimberli is a Family Nurse Practitioner who has been practicing since 2019 in the areas of Family Medicine and Mental Health. She began her career as an LPN in 2010, obtained her RN in 2013, and further advanced to becoming a Nurse Practitioner six years later.',
				},
				{
					provider: 'theresa',
					name: 'Theresa Vergara, NP',
					bio: 'Theresa is a Certified Nurse Practitioner with a Doctorate in Nursing from Columbia University. She also holds a Masters in Human Nutrition, with a focus on the interplay between sleep and food intake.',
				},
				{
					provider: 'craig',
					name: 'Craig Sorkin, NP',
					bio: 'Following the events of September 11, 2001, Craig began his healthcare career by joining a local first-aid squad, which inspired a lifelong dedication to patient care. An early advocate for technology in medicine, he embraced digital healthcare tools during his training and developed a lasting interest in healthcare innovation.',
				},
			],
		}),

		LandingKovaV2Benefits: (): LandingComponentProps.KovaV2Benefits => ({
			title: 'Why Kova',
			items: [
				'No insurance required',
				'No in-person clinic visit',
				'Online clinician support',
				'Medication shipped to your door',
				'Ongoing support throughout treatment',
			],
			chips: [
				{ icon: 'shipping', label: 'Free shipping' },
				{ icon: 'star', label: 'Transparent pricing' },
			],
			centered: false,
			ctaLabel: 'Check my eligibility',
		}),

		LandingKovaV2Outcomes: (): LandingComponentProps.KovaV2Outcomes => ({
			stat: { value: '-20%', label: 'body weight*' },
			title: 'Get the right prescription for long-term results',
			claimParts: [
				{ text: 'In clinical studies, patients ' },
				{ text: 'lost up to 20% of their body weight', accent: true },
				{ text: ' with the help of prescription weight-loss medications.' },
			],
			footnote:
				'*Based on Semaglutide Weight Management Study. Results are individual and vary based on the medication prescribed, your health history, and your consistency with the program. Images are for illustrative purposes only. Actual medication appearance and packaging may vary.',
		}),

		LandingKovaFaq: (): LandingComponentProps.KovaFaq => ({
			theme: 'v2',
			title: "We've got you.",
			subtitle: 'You have questions, we have answers.',
			items: [
				{
					question: 'How does shipping work?',
					answer: [
						"If prescribed, your medication and supplies are shipped discreetly in temperature-controlled packaging. You'll receive tracking information once your order ships, and future shipments are timed around your medication schedule.",
					],
				},
				{
					question: 'Is this covered by insurance or HSA/FSA?',
					answer: [
						'Compounded medications are generally not covered by insurance. You may be able to use HSA or FSA funds for eligible expenses, but eligibility varies by plan. Check with your HSA or FSA provider to confirm.',
					],
				},
				{
					question: 'Can I cancel?',
					answer: [
						'Yes. Your plan renews automatically at the end of each plan period, but you can cancel anytime before your next renewal through your account. See the billing details below for more information.',
					],
				},
				{
					question: 'What should I know about safety and side effects?',
					answer: [
						'GLP-1 medications can cause side effects, including nausea and digestive changes, especially when starting treatment or adjusting doses. A licensed clinician will review your health information, discuss potential risks and side effects, and determine whether compounded semaglutide or tirzepatide is appropriate for you.',
					],
					note: "Compounded medications aren’t FDA-approved and haven’t been reviewed by the FDA for safety, effectiveness, or quality. Use the Kova app to track how you're feeling and share any concerns with your clinician.",
				},
				{
					question: 'How is eligibility determined?',
					answer: [
						'You pay at checkout. A licensed clinician reviews every order; if treatment isn’t approved — a full refund, automatically.',
					],
				},
				{
					question: 'Billing & cancellation details',
					answer: [],
					sections: [
						{
							title: 'Your plan renews automatically',
							text: 'Your plan renews at the end of each plan period (1, 3, 6 or 12 months) at the current price for that plan.',
						},
						{
							title: 'How to cancel',
							text: 'Cancel anytime before your renewal date through your account settings or by contacting support. Cancellation stops future charges. Once your medication has shipped, the current plan period is nonrefundable.',
						},
						{
							title: 'If the price changes',
							text: "If your plan price changes, we'll email you before your next renewal so you can review the new price or cancel before you're charged.",
						},
						{
							title: 'What your plan includes',
							text: 'Your plan price includes your consultation, medication, supplies, shipping, and Kova app access. There are no separate mandatory membership or dispensing fees.',
						},
					],
				},
			],
		}),

		LandingKovaV2FinalOffer: (): LandingComponentProps.KovaV2FinalOffer => ({
			// Node 6438:61912 / 6566:35203: "Get started for only " + the green price (frame typo fixed).
			titleParts: [{ text: 'Get started for only ' }, { text: '$129/month', accent: true }],
			text: 'Lose up to 20% of your body weight with medically supervised safe & effective treatment from the comfort of your home.',
			ctaLabel: 'Check my eligibility',
		}),

		LandingKovaFooter: (): LandingComponentProps.KovaFooter => ({
			theme: 'v2',
			layout: 'columns',
			madeInUsa: true,
			tagline:
				'Your daily support through GLP-1 treatment: medication, tracking, and clinician-aligned care in one place.',
			// The same destinations as the /app footer (`instances/areas/glp-app/index.ts`).
			links: [
				{ href: 'https://trykova.com/hipaa/', label: 'Notice of Privacy Practices' },
				{
					href: 'https://trykova.com/telehealth-medical-consent/',
					label: 'Consumer Health Data Privacy Notice',
				},
				{ href: 'https://trykova.com/terms/', label: 'Notice, Terms and Conditions' },
				{ href: 'https://trykova.com/privacy/', label: 'Privacy Notice' },
			],
			contacts: { email: 'support@trykova.com', phone: '+1 831 480 7153' },
			entity: [
				'LIFETECH APPS INC',
				'Registration number 10554303',
				'',
				'Registered Office:',
				'501 SILVERSIDE ROAD, SUITE 105',
				'WILMINGTON, NEW CASTLE,',
				'DELAWARE, ZIPCODE 19809',
			],
			disclaimer:
				'Compounded semaglutide and tirzepatide are not FDA-approved and have not been reviewed by the FDA for safety, effectiveness, or quality. They are prepared by a state-licensed or FDA-registered compounding facility based on a prescription from a licensed clinician. GLP-1 medications are prescription-only and appropriate only for eligible individuals. Individual results vary and are not guaranteed. This page provides general information, not medical advice. Talk to your clinician about whether treatment is right for you.',
		}),
	},
}

export default schema
